import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Loader2, Lock, LogOut, Plus, Pencil, Trash2, Save, X, Upload,
  Search, Wallet, Users, Car, FileText, IndianRupee, Calendar, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  'Life', 'Health', 'Vehicle / Motor', 'Fire', 'Marine', 'Property',
  'Professional Indemnity', 'Cyber Liability', 'Group Health', 'Group Life',
  'Workmen Compensation', 'Other',
];
const FREQUENCIES = ['Monthly', 'Quarterly', 'Half-Yearly', 'Yearly', 'Single'];
const STATUSES   = ['Active', 'Lapsed', 'Matured', 'Cancelled'];

const STATUS_TONE = {
  'Active':    'bg-green-100 text-green-800 border-green-200',
  'Lapsed':    'bg-red-100 text-red-800 border-red-200',
  'Matured':   'bg-blue-100 text-blue-800 border-blue-200',
  'Cancelled': 'bg-gray-100 text-gray-700 border-gray-200',
};

const money = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const toISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
};
const fromISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};
const daysUntil = (ddmmyyyy) => {
  const iso = toISODate(ddmmyyyy);
  if (!iso) return null;
  const then = new Date(iso + 'T00:00:00');
  const now  = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((then - now) / (1000 * 60 * 60 * 24));
};

const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' · ');
  return String(detail);
};

const emptyPolicy = () => ({
  policy_name: '',
  policy_number: '',
  insurer: '',
  insurance_type: 'Fire',
  sum_assured: 0,
  premium_amount: 0,
  premium_frequency: 'Yearly',
  start_date: '',
  maturity_date: '',
  next_renewal_date: '',
  coverage: '',
  nominee: '',
  assigned_to_employee_id: '',
  status: 'Active',
  document_path: null,
  remarks: '',
  sort_order: 100,
});

const FieldGroup = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

const InsurancesPage = () => {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();

  const [policies, setPolicies] = useState([]);
  const [employees, setEmps]    = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const [q, setQ]           = useState('');
  const [typeFilter, setTypeFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const docRef = useRef(null);

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/admin/insurances', { replace: true });
  }, [ready, user, navigate]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [{ data: p }, { data: emps }] = await Promise.all([
        axios.get(`${API}/admin/insurances`),
        axios.get(`${API}/admin/employees?limit=500`),
      ]);
      setPolicies(Array.isArray(p) ? p : []);
      setEmps(Array.isArray(emps) ? emps : []);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (ready && user) load(); /* eslint-disable-next-line */ }, [ready, user]);

  const empMap = useMemo(() => {
    const m = new Map();
    employees.forEach((e) => m.set(e.id, e));
    return m;
  }, [employees]);

  const activeEmployees = useMemo(
    () => employees.filter((e) => !e.date_of_leaving).sort((a, b) => a.name.localeCompare(b.name)),
    [employees]
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return policies.filter((p) => {
      if (typeFilter && p.insurance_type !== typeFilter) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      if (needle) {
        const emp = empMap.get(p.assigned_to_employee_id)?.name || '';
        const hay = `${p.policy_name} ${p.policy_number || ''} ${p.insurer || ''} ${p.insurance_type} ${p.nominee || ''} ${emp} ${p.remarks || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [policies, q, typeFilter, statusFilter, empMap]);

  const stats = useMemo(() => {
    let sum = 0, prem = 0, active = 0, dueSoon = 0;
    for (const p of policies) {
      sum  += Number(p.sum_assured) || 0;
      prem += Number(p.premium_amount) || 0;
      if (p.status === 'Active') active++;
      const d = daysUntil(p.next_renewal_date);
      if (d !== null && d <= 30 && p.status === 'Active') dueSoon++;
    }
    return { total: policies.length, active, dueSoon, sum, prem };
  }, [policies]);

  const openNew  = () => { setEditing(emptyPolicy()); setError(''); };
  const openEdit = (p) => { setEditing({ ...emptyPolicy(), ...p }); setError(''); };
  const cancel   = () => { setEditing(null); setError(''); };
  const handleField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const uploadDoc = async (file) => {
    if (!file) return;
    setDocUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/insurances/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, document_path: data.path }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Upload failed');
    } finally { setDocUploading(false); }
  };

  const save = async () => {
    if (!editing.policy_name?.trim()) { setError('Policy Name is required'); return; }
    if (!editing.insurance_type)      { setError('Insurance Type is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        policy_name: editing.policy_name.trim(),
        policy_number: editing.policy_number || null,
        insurer: editing.insurer || null,
        insurance_type: editing.insurance_type,
        sum_assured: Number(editing.sum_assured) || 0,
        premium_amount: Number(editing.premium_amount) || 0,
        premium_frequency: editing.premium_frequency || 'Yearly',
        start_date: editing.start_date || null,
        maturity_date: editing.maturity_date || null,
        next_renewal_date: editing.next_renewal_date || null,
        coverage: editing.coverage || null,
        nominee: editing.nominee || null,
        assigned_to_employee_id: editing.assigned_to_employee_id || null,
        status: editing.status || 'Active',
        document_path: editing.document_path || null,
        remarks: editing.remarks || null,
        sort_order: Number(editing.sort_order) || 100,
      };
      if (editing.id) await axios.put(`${API}/admin/insurances/${editing.id}`, payload);
      else            await axios.post(`${API}/admin/insurances`, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete policy "${p.policy_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/insurances/${p.id}`);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    }
  };

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
        <Loader2 className="text-brand-yellow animate-spin" size={22} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="insurances-page">
      {/* Admin bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30 print:hidden">
        <div className="max-w-[1400px] mx-auto px-[7.6923%] py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
              <Lock size={14} className="text-brand-yellow" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">
                Admin Dashboard
              </div>
              <div className="text-sm font-medium">Insurances · {policies.length} policies</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openNew} className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 rounded-none h-9 px-4 text-sm font-semibold" data-testid="add-insurance-btn">
              <Plus size={16} className="mr-1.5" /> Add Policy
            </Button>
            <button onClick={() => navigate('/team')}          className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"><Users size={13} /> Team</button>
            <button onClick={() => navigate('/admin/cashflow')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"><Wallet size={13} /> Cashflow</button>
            <button onClick={() => navigate('/admin/vehicles')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"><Car size={13} /> Vehicles</button>
            <button onClick={() => navigate('/admin/payroll')}  className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors"><span className="font-mono">₹</span> Payroll</button>
            <button onClick={() => navigate('/admin/policies')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" data-testid="nav-policies-btn"><span aria-hidden>📘</span> Policies</button>
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold transition-colors px-3 py-2"
              data-testid="insurances-logout-btn"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative bg-[#0a0f1e] pt-12 pb-14 overflow-hidden print:hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-brand-yellow/15 blur-[160px] rounded-full pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
          <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-4">
            <Shield size={13} className="text-brand-yellow" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
              Internal · R S T &amp; Co.
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-black text-white leading-[1.02] tracking-tight">
            Insurance Register
          </h1>
          <p className="text-sm md:text-base text-white/60 mt-3 max-w-2xl">
            Every firm and group policy — premiums, renewals, sum assured, coverage and documents in one place.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-8">
            <KPI label="Total Policies"       value={String(stats.total)}       icon={<Shield size={14} />} tone="mono" />
            <KPI label="Active"               value={String(stats.active)}      icon={<Shield size={14} />} tone="green" />
            <KPI label="Renewal Due (≤30d)"   value={String(stats.dueSoon)}     icon={<AlertTriangle size={14} />} tone={stats.dueSoon > 0 ? 'red' : 'mono'} />
            <KPI label="Total Sum Assured"    value={`₹${money(stats.sum)}`}   icon={<IndianRupee size={14} />} tone="yellow" />
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-200 sticky top-[60px] z-20 print:hidden">
        <div className="max-w-[1400px] mx-auto px-[7.6923%] py-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search policy, insurer, nominee…"
              className="w-full border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-gray-900"
              data-testid="insurance-search" />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-gray-900" data-testid="insurance-type-filter">
            <option value="">All Types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-gray-900" data-testid="insurance-status-filter">
            <option value="">All Statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </section>

      {/* Table */}
      <section className="max-w-[1400px] mx-auto px-[7.6923%] py-8">
        {error && !editing && (
          <div className="border border-red-300 bg-red-50 text-red-800 text-sm px-4 py-2 mb-4">{error}</div>
        )}
        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <Loader2 className="mx-auto mb-3 animate-spin" size={22} />
            Loading policies…
          </div>
        ) : filtered.length === 0 ? (
          <div className="border-2 border-dashed border-gray-200 py-20 text-center">
            <Shield size={30} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm mb-4">
              {policies.length === 0 ? 'No policies yet.' : 'No policies match the current filters.'}
            </p>
            {policies.length === 0 && (
              <Button onClick={openNew} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="add-first-insurance-btn">
                <Plus size={14} className="mr-1.5" /> Add first policy
              </Button>
            )}
          </div>
        ) : (
          <div className="border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 text-white text-[10px] font-mono tracking-widest uppercase sticky top-[128px] z-10 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.35)]">
                <tr>
                  <th className="text-left px-3 py-3">Policy</th>
                  <th className="text-left px-3 py-3 hidden md:table-cell">Type</th>
                  <th className="text-left px-3 py-3 hidden lg:table-cell">Insurer</th>
                  <th className="text-right px-3 py-3">Sum Assured</th>
                  <th className="text-right px-3 py-3 hidden md:table-cell">Premium</th>
                  <th className="text-left px-3 py-3 hidden lg:table-cell">Renewal</th>
                  <th className="text-left px-3 py-3 hidden xl:table-cell">Assigned</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-right px-3 py-3 w-24 print:hidden"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const emp     = empMap.get(p.assigned_to_employee_id);
                  const daysLeft = daysUntil(p.next_renewal_date);
                  const tone =
                    daysLeft === null ? 'text-gray-400' :
                    daysLeft < 0 ? 'text-red-700 font-semibold' :
                    daysLeft <= 30 ? 'text-amber-700 font-semibold' :
                    'text-gray-700';
                  return (
                    <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50/70 align-top" data-testid={`insurance-row-${p.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{p.policy_name}</div>
                        {p.policy_number && <div className="text-[10px] font-mono text-gray-500">#{p.policy_number}</div>}
                        {p.document_path && (
                          <a href={`${API}/media/${p.document_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-brand-blue hover:underline mt-0.5">
                            <FileText size={10} /> Policy doc
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell text-gray-700 text-xs">{p.insurance_type}</td>
                      <td className="px-3 py-2 hidden lg:table-cell text-gray-700 text-xs">{p.insurer || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900 font-semibold">₹ {money(p.sum_assured)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 hidden md:table-cell">
                        ₹ {money(p.premium_amount)}
                        <div className="text-[10px] text-gray-400">{p.premium_frequency}</div>
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        {p.next_renewal_date ? (
                          <>
                            <div className={`text-xs ${tone}`}>{p.next_renewal_date}</div>
                            <div className="text-[10px] text-gray-500">
                              {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` :
                               daysLeft === 0 ? 'Today' :
                               `${daysLeft}d left`}
                            </div>
                          </>
                        ) : <span className="text-xs text-gray-400 italic">Not set</span>}
                      </td>
                      <td className="px-3 py-2 hidden xl:table-cell text-xs">
                        {emp ? emp.name : <span className="text-gray-400 italic">Firm-wide</span>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block text-[11px] px-2 py-0.5 border ${STATUS_TONE[p.status] || STATUS_TONE.Active}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 print:hidden">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => openEdit(p)} className="p-1.5 border border-gray-200 hover:border-gray-900 hover:bg-gray-100 transition-colors" title="Edit" data-testid={`edit-insurance-${p.id}`}>
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => remove(p)}   className="p-1.5 border border-red-100 text-red-600 hover:border-red-500 hover:bg-red-50 transition-colors" title="Delete" data-testid={`delete-insurance-${p.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t border-gray-200 text-xs font-mono">
                <tr>
                  <td className="px-3 py-2 uppercase tracking-widest text-gray-600" colSpan={3}>Totals · {filtered.length}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">₹ {money(filtered.reduce((a, r) => a + (Number(r.sum_assured) || 0), 0))}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold hidden md:table-cell">₹ {money(filtered.reduce((a, r) => a + (Number(r.premium_amount) || 0), 0))}</td>
                  <td className="hidden lg:table-cell"></td>
                  <td className="hidden xl:table-cell"></td>
                  <td></td>
                  <td className="print:hidden"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex justify-end print:hidden" onClick={cancel} data-testid="insurance-editor">
          <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[9px] tracking-widest text-brand-yellow uppercase">
                  {editing.id ? 'Edit policy' : 'Add policy'}
                </div>
                <div className="font-heading text-xl font-bold text-gray-900">
                  {editing.policy_name || 'New Policy'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={save} disabled={saving} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="save-insurance-btn">
                  {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save</>}
                </Button>
                <button onClick={cancel} className="p-2 border border-gray-200 hover:border-gray-900 hover:bg-gray-100" title="Close" data-testid="close-insurance-editor">
                  <X size={16} />
                </button>
              </div>
            </div>

            {error && (
              <div className="mx-6 mt-4 border border-red-300 bg-red-50 text-red-800 text-sm px-3 py-2">
                {error}
              </div>
            )}

            <div className="p-6 space-y-5">
              <FieldGroup label="Policy Name*">
                <input value={editing.policy_name} onChange={(e) => handleField('policy_name', e.target.value)}
                  className="admin-input" placeholder="e.g. Office Fire Cover 2026-27"
                  data-testid="editor-policy-name" />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Policy Number">
                  <input value={editing.policy_number || ''} onChange={(e) => handleField('policy_number', e.target.value)}
                    className="admin-input font-mono" data-testid="editor-policy-number" />
                </FieldGroup>
                <FieldGroup label="Insurer">
                  <input value={editing.insurer || ''} onChange={(e) => handleField('insurer', e.target.value)}
                    className="admin-input" placeholder="LIC / ICICI Lombard / HDFC Ergo…"
                    data-testid="editor-insurer" />
                </FieldGroup>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FieldGroup label="Type*">
                  <select value={editing.insurance_type} onChange={(e) => handleField('insurance_type', e.target.value)}
                    className="admin-input" data-testid="editor-insurance-type">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </FieldGroup>
                <FieldGroup label="Sum Assured">
                  <MoneyInput value={editing.sum_assured} onChange={(v) => handleField('sum_assured', v)} testId="editor-sum-assured" />
                </FieldGroup>
                <FieldGroup label="Status">
                  <select value={editing.status} onChange={(e) => handleField('status', e.target.value)}
                    className="admin-input" data-testid="editor-status">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </FieldGroup>
              </div>

              {/* Premium */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <IndianRupee size={12} /> Premium
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldGroup label="Premium Amount">
                    <MoneyInput value={editing.premium_amount} onChange={(v) => handleField('premium_amount', v)} testId="editor-premium-amount" />
                  </FieldGroup>
                  <FieldGroup label="Frequency">
                    <select value={editing.premium_frequency} onChange={(e) => handleField('premium_frequency', e.target.value)}
                      className="admin-input" data-testid="editor-premium-frequency">
                      {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </FieldGroup>
                </div>
              </div>

              {/* Dates */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <Calendar size={12} /> Period &amp; Renewals
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FieldGroup label="Start Date">
                    <input type="date"
                      value={toISODate(editing.start_date)}
                      onChange={(e) => handleField('start_date', fromISODate(e.target.value))}
                      className="admin-input" data-testid="editor-start-date" />
                  </FieldGroup>
                  <FieldGroup label="Maturity Date">
                    <input type="date"
                      value={toISODate(editing.maturity_date)}
                      onChange={(e) => handleField('maturity_date', fromISODate(e.target.value))}
                      className="admin-input" data-testid="editor-maturity-date" />
                  </FieldGroup>
                  <FieldGroup label="Next Renewal Date">
                    <input type="date"
                      value={toISODate(editing.next_renewal_date)}
                      onChange={(e) => handleField('next_renewal_date', fromISODate(e.target.value))}
                      className="admin-input" data-testid="editor-next-renewal" />
                  </FieldGroup>
                </div>
              </div>

              {/* Coverage & Nominee */}
              <FieldGroup label="Coverage Details">
                <textarea value={editing.coverage || ''} onChange={(e) => handleField('coverage', e.target.value)}
                  placeholder="What is covered under this policy, exclusions, add-ons…"
                  className="admin-input min-h-[80px]" data-testid="editor-coverage" />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Nominee">
                  <input value={editing.nominee || ''} onChange={(e) => handleField('nominee', e.target.value)}
                    className="admin-input" placeholder="Beneficiary name" data-testid="editor-nominee" />
                </FieldGroup>
                <FieldGroup label="Assigned to Employee">
                  <select value={editing.assigned_to_employee_id || ''} onChange={(e) => handleField('assigned_to_employee_id', e.target.value)}
                    className="admin-input" data-testid="editor-assigned">
                    <option value="">— Firm-wide —</option>
                    {activeEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.name} · {emp.designation}</option>
                    ))}
                  </select>
                </FieldGroup>
              </div>

              {/* Document */}
              <FieldGroup label="Policy Document">
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden text-center px-2">
                    {editing.document_path ? (
                      <a href={`${API}/media/${editing.document_path}`} target="_blank" rel="noreferrer" className="text-xs text-brand-blue hover:underline">
                        <FileText size={20} className="mx-auto mb-1 text-gray-600" />
                        View
                      </a>
                    ) : (
                      <FileText size={22} className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input ref={docRef} type="file" accept="application/pdf,image/*" className="hidden"
                      onChange={(e) => uploadDoc(e.target.files?.[0])} />
                    <Button type="button" onClick={() => docRef.current?.click()} disabled={docUploading}
                      className="rounded-none bg-gray-900 hover:bg-black text-white text-xs h-9" data-testid="upload-insurance-doc-btn">
                      {docUploading
                        ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
                        : <><Upload size={14} className="mr-2" /> Upload / Replace</>}
                    </Button>
                    <p className="text-[11px] text-gray-500 mt-1">PDF or Image · up to 20 MB.</p>
                  </div>
                </div>
              </FieldGroup>

              <FieldGroup label="Remarks">
                <textarea value={editing.remarks || ''} onChange={(e) => handleField('remarks', e.target.value)}
                  placeholder="Renewal reminders, contact person at insurer, endorsements…"
                  className="admin-input min-h-[68px]" data-testid="editor-remarks" />
              </FieldGroup>

              <FieldGroup label="Sort Order" className="max-w-[200px]">
                <input type="number" value={editing.sort_order ?? 100} onChange={(e) => handleField('sort_order', e.target.value)}
                  className="admin-input" />
              </FieldGroup>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-input {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 8px 10px;
          font-size: 14px;
          outline: none;
          background: white;
        }
        .admin-input:focus { border-color: #111827; }
      `}</style>
    </div>
  );
};

const MoneyInput = ({ value, onChange, testId }) => (
  <div className="relative">
    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
    <input type="number" min="0" step="1" value={value ?? 0}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 pl-6 pr-2 py-2 text-sm tabular-nums text-right focus:outline-none focus:border-gray-900"
      data-testid={testId} />
  </div>
);

const KPI = ({ label, value, icon, tone }) => {
  const toneMap = {
    yellow: 'text-brand-yellow',
    green:  'text-green-400',
    amber:  'text-amber-400',
    red:    'text-red-400',
    mono:   'text-white',
  };
  return (
    <div className="bg-[#0a0f1e] p-5">
      <div className="flex items-center gap-2 mb-2 text-white/60">
        {icon}
        <span className="font-mono text-[10px] tracking-widest uppercase">{label}</span>
      </div>
      <div className={`font-heading text-2xl md:text-3xl font-black tabular-nums ${toneMap[tone] || toneMap.mono}`}>{value}</div>
    </div>
  );
};

export default InsurancesPage;
